import type { en } from "./en.ts";

export const fr: typeof en = {
    meetingStates: {
        scheduled: "Planifié",
        draft: "À réviser",
        ready: "Ordre du jour consulté",
        completed: "Terminé"
    },
    meetingTemplates: {
        default: "Suivi simple",
        compensation: "Rémunération",
        departure: "Départ et fin d’emploi",
        difficult_conversation: "Conversation difficile",
        goal_setting: "Définition des objectifs et planification",
        growth_discussion: "Discussion sur le développement",
        new_hire: "Nouvelle recrue",
        performance_conversation: "Conversation sur la performance",
        promotion: "Promotion",
        return_from_leave: "Retour d’un congé prolongé",
        skip_level: "Rencontre interniveau",
        team_alignment: "Alignement de l’équipe"
    },
    MeetingsTable: {
        ariaLabel: "Réunions",
        untitledMeeting: "Réunion sans titre",
        columns: {
            meeting: "Réunion",
            when: "Quand",
            duration: "Durée",
            status: "Statut",
            actions: "Actions"
        },
        meetingTypes: {
            OneOnOne: "1:1",
            Team: "Équipe"
        },
        recap: {
            present: "Récapitulatif",
            absent: "Aucun récapitulatif"
        }
    },
    MeetingRowActions: {
        ariaLabel: "Actions pour {{title}}",
        untitledMeeting: "Réunion sans titre",
        openInCalendar: "Ouvrir dans le calendrier",
        opensInNewTab: "S'ouvre dans un nouvel onglet",
        delete: "Supprimer",
        cancel: "Annuler",
        deleteDialog: {
            title: "Supprimer cet ordre du jour ?",
            description: "Les points de discussion, les notes privées et le récapitulatif de « {{title}} » seront supprimés. Cette action est irréversible. L’événement du calendrier est conservé, ainsi que les éléments d’action créés à partir de cette réunion."
        },
        toast: {
            deleted: "Ordre du jour supprimé",
            deleteError: "Impossible de supprimer l’ordre du jour. Réessayez."
        }
    },
    MeetingsToolbar: {
        searchPlaceholder: "Rechercher des réunions et leur contenu",
        searchAriaLabel: "Rechercher des réunions et leur contenu",
        state: {
            all: "Tous les statuts",
            ariaLabel: "Filtrer par statut",
            clearAriaLabel: "Effacer le filtre de statut"
        },
        conversationType: {
            all: "Tous les types de conversation",
            ariaLabel: "Filtrer par type de conversation",
            clearAriaLabel: "Effacer le filtre de type de conversation"
        },
        when: {
            all: "Toutes les réunions",
            upcoming: "À venir",
            last30Days: "30 derniers jours",
            customRange: "Période personnalisée",
            ariaLabel: "Filtrer par date",
            clearAriaLabel: "Effacer le filtre de date",
            rangeLabel: "Période personnalisée",
            clearTimeRange: "Effacer la période"
        },
        resetFilters: "Effacer les filtres"
    },
    MeetingsGapBanner: {
        message_one: "{{count}} de vos collaborateurs directs n’a pas de 1:1 planifié",
        message_other: "{{count}} de vos collaborateurs directs n’ont pas de 1:1 planifié",
        scheduleOneOnOne: "Planifier un 1:1"
    },
    MeetingNotFound: {
        heading: "Réunion introuvable",
        body: "Cette réunion n'existe pas ou ne figure plus à votre agenda.",
        backToMeetings: "Retour aux réunions"
    },
    MeetingAdditionalContext: {
        heading: "Ordre du jour",
        caption: "Votre agent rédigera cet ordre du jour le matin du {{date}} et conservera tout ce que vous écrivez ici.",
        editorLabel: "Ce que vous voulez aborder",
        placeholder: "Ajoutez ce que vous voulez aborder",
        empty: "Rien n’a encore été ajouté.",
        autoSave: {
            saving: "Enregistrement…",
            saved: "Toutes les modifications sont enregistrées",
            refused: "Non enregistré — raccourcissez votre texte",
            error: "Modifications non enregistrées"
        }
    },
    MeetingAgenda: {
        heading: "Ordre du jour",
        empty: "Aucun ordre du jour pour l’instant.",
        editorLabel: "Ordre du jour de la réunion",
        placeholder: "Rédigez l’ordre du jour de cette réunion.",
        privateNoteLabel: "Vous seul · Masqué pour le participant",
        askAi: {
            askAi: "Demander à l’agent",
            input: "Demander à l’agent de modifier cette partie de l’ordre du jour",
            inputPlaceholder: "Demandez-moi de modifier…",
            send: "Envoyer",
            close: "Fermer"
        },
        citations: {
            pillLabel: "Source {{index}} : {{title}}",
            authorAndDate: "{{author}} • {{date}}"
        },
        autoSave: {
            saving: "Enregistrement…",
            saved: "Toutes les modifications sont enregistrées",
            refused: "Non enregistré — raccourcissez l’ordre du jour",
            error: "Modifications non enregistrées"
        }
    },
    MeetingRecap: {
        heading: "Récapitulatif",
        syncedFrom: "Synchronisé depuis {{providers}}",
        manualEdit: "Modifié manuellement par le propriétaire",
        notCollectedYet: "Aucun récapitulatif — vos applications de prise de notes seront consultées à la fin de la réunion.",
        states: {
            Polling: "Récupération de votre récapitulatif",
            Complete: "Aucun récapitulatif pour l’instant.",
            NothingFound: "Rien n’a été enregistré pour cette réunion",
            ProvidersUnreachable: "Impossible de joindre vos applications de prise de notes."
        },
        pollingDescription: "Workleap cherche la transcription de cette réunion dans vos applications de prise de notes et continue de vérifier jusqu’à une heure après la fin prévue de la réunion. Le récapitulatif apparaîtra ici de lui-même ; vous pouvez quitter la page.",
        nothingFoundDescription: "Workleap n’a trouvé aucune transcription de cette réunion dans vos applications de prise de notes et a cessé de vérifier.",
        editWithAgent: {
            label: "Modifier le récapitulatif",
            suggestions: {
                shorten: {
                    label: "Raccourcir",
                    prompt: "Raccourcis le récapitulatif — ne garde que les décisions et les prochaines étapes."
                },
                show: {
                    label: "Montrer les transcriptions",
                    prompt: "Montre-moi les transcriptions."
                },
                redo: {
                    label: "Refaire le récapitulatif à partir de ce qui est disponible maintenant",
                    prompt: "Refais le récapitulatif à partir de ce qui est disponible maintenant."
                }
            }
        }
    },
    MeetingContextPanel: {
        evidenceWindow: "Activité du {{start}} au {{end}}",
        heading: "Contexte",
        evidence_one: "Source ({{count}})",
        evidence_other: "Sources ({{count}})"
    },
    MeetingCitationSourceTypes: {
        Performance: "Performance",
        Officevibe: "Officevibe",
        Slack: "Slack",
        Confluence: "Confluence",
        SharePoint: "SharePoint",
        Jira: "Jira",
        Goal: "Objectif",
        Review: "Évaluation",
        Recognition: "Bonnes Vibes",
        FeedbackConversation: "Feedback",
        Meeting: "Réunion"
    },
    AskAiButton: {
        inputLabel: "Que doit modifier l’agent?",
        inputPlaceholder: "Que voulez-vous changer?",
        send: "Envoyer",
        suggestionsLabel: "Suggestions"
    },
    MeetingTemplatePicker: {
        ariaLabel: "Modèles d’ordre du jour",
        noTemplate: "Choisir un modèle",
        suggestedBadge: "Suggéré",
        autoMatched: "Choix automatique",
        caption: "Brouillon rédigé par votre agent à partir de l’activité récente de {{name}}, avec",
        captionWithoutCounterpart: "Brouillon rédigé par votre agent à partir de l’activité récente, avec",
        prompt: "Prépare l’ordre du jour de ma prochaine réunion avec le modèle « {{template}} »",
        manageTemplates: "Gérer les modèles",
        editWithAgent: {
            label: "Demander des modifications…",
            suggestions: {
                shorten: {
                    label: "Raccourcir",
                    prompt: "Raccourcis l’ordre du jour — ne garde que les sujets les plus importants."
                }
            }
        }
    },
    MeetingDuration: {
        minutes_one: "{{count}} minute",
        minutes_other: "{{count}} minutes",
        hours_one: "{{count}} heure",
        hours_other: "{{count}} heures",
        hoursAndMinutes: "{{hours}} h {{minutes}}"
    },
    MeetingSwitcher: {
        triggerAriaLabel: "Changer de réunion — actuellement {{schedule}}",
        sections: {
            upcoming: "À venir",
            past: "Passées"
        },
        today: "Aujourd'hui",
        currentMeeting: "Ouverte en ce moment",
        loading: "Chargement des réunions…",
        empty: "Aucune réunion.",
        error: "Nous n'avons pas pu charger vos réunions pour l'instant.",
        seeAllMeetings: "Voir toutes les réunions"
    },
    MeetingsWidget: {
        heading: {
            today: "Aujourd'hui",
            upcoming: "À venir"
        },
        oneOnOneBadge: "1:1",
        empty: "Aucune réunion à venir.",
        overdueActionItems_one: "{{count}} tâche en retard",
        overdueActionItems_other: "{{count}} tâches en retard",
        createMeeting: {
            label: "Créer une réunion",
            prompt: "Planifie une réunion"
        },
        notConnected: {
            heading: "Aucun calendrier connecté",
            body: "Connectez Outlook ou Google pour voir votre prochaine réunion.",
            link: "Connecter le calendrier"
        },
        expired: {
            heading: "Connexion au calendrier expirée",
            body: "Reconnectez Outlook ou Google pour voir votre prochaine réunion.",
            link: "Reconnecter le calendrier"
        },
        error: "Nous n'avons pas pu charger votre prochaine réunion pour le moment.",
        liveRegion: {
            loading: "Chargement…"
        }
    },
    WeeklyCalendar: {
        title: "La semaine à venir",
        empty: "Aucun calendrier connecté — connectez Outlook ou Google pour voir vos 1:1 et vos suivis cette semaine.",
        connectCalendar: "Connecter mon calendrier",
        expired: "La connexion à votre calendrier a expiré — reconnectez Outlook ou Google pour voir vos 1:1 et vos suivis cette semaine.",
        reconnectCalendar: "Reconnecter mon calendrier",
        refreshPrompt: "Actualisez la page pour voir le calendrier mis à jour.",
        refresh: "Actualiser",
        error: "Nous n'avons pas pu charger vos réunions pour le moment.",
        loading: "Chargement de votre semaine…",
        previousWeek: "Semaine précédente",
        nextWeek: "Semaine suivante",
        returnToThisWeek: "Revenir à cette semaine",
        today: "Aujourd'hui",
        typeLabel: "Type",
        allMeetings: "Toutes les réunions",
        oneOnOnes: "1:1",
        groupMeetings: "Réunions de groupe",
        otherMeetings: "Tout le reste",
        multipleTypes: "{{typeCount}} types de réunions",
        peopleLabel: "Personnes",
        everyone: "Tout le monde",
        directReports: "Mes subordonnés directs",
        noMeetings: "Aucune réunion planifiée cette semaine.",
        noFilteredMeetings: "Aucune réunion ne correspond à ces filtres cette semaine.",
        showWeekends: "Afficher les fins de semaine",
        timeZoneInfoLabel: "Fuseau horaire",
        timeZoneInfo: "Les réunions sont affichées à l'heure {{timeZone}}.",
        meetingLinkLabel: "Ouvrir {{subject}} à {{time}}"
    },
    OrganizeMeetingsButton: {
        label: "Gérer mon calendrier",
        flagOneOnOnes: {
            label: "Signaler les 1:1 manquants ou en retard",
            prompt: "Signale les 1:1 manquants ou en retard"
        },
        bookMeeting: {
            label: "Planifier une réunion",
            prompt: "Planifie une réunion"
        },
        bookMeetingWithPerson: {
            label: "Planifier une réunion avec {{name}}",
            prompt: "Je veux planifier une réunion avec {{name}}"
        }
    },
    ConnectCalendarLink: {
        opensInNewTab: "S'ouvre dans un nouvel onglet"
    },
    MeetingErrors: {
        retry: "Réessayer",
        retrying: "Nouvel essai…",
        reassurance: "Veuillez réessayer plus tard.",
        refreshBanner: "Impossible d'actualiser — affichage des derniers résultats."
    }
};
